import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  Calendar,
  Check,
  ChevronLeft,
  Clock,
  Copy,
  Download,
  ExternalLink,
  FileText,
  FolderInput,
  Globe,
  Link as LinkIcon,
  List,
  Loader2,
  Maximize2,
  Minimize2,
  Pencil,
  Plus,
  Share2,
  Star,
  Tag,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

import "./BookmarkDetail.scss";
import BookmarkPreview from "../../assets/icons/bookmarksPreviews.svg";
import ShareCard from "../../shades/cards/shareCard";
import { toastify } from "../Toast";
import services from "../../API";

function resolveTheme() {
  const selectedTheme = localStorage.getItem("theme");

  if (selectedTheme === "dark" || selectedTheme === "light") {
    return selectedTheme;
  }

  if (document.documentElement.classList.contains("dark")) return "dark";
  if (document.documentElement.classList.contains("light")) return "light";

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function getErrorMessage(error, fallback = "Something went wrong. Please try again.") {
  return error?.response?.data?.message || fallback;
}

function getDomain(url = "") {
  try {
    return new URL(url).hostname.replace(/^www\./, "");
  } catch {
    return url.replace(/^https?:\/\//, "").split("/")[0];
  }
}

function formatDate(date) {
  if (!date) return "—";
  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return "—";

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsedDate);
}

function getPrimaryImage(card) {
  const images = card?.content?.imageUrl;
  if (Array.isArray(images)) {
    return images.find((item) => item?.url)?.url || BookmarkPreview;
  }
  return images?.url || BookmarkPreview;
}

function isEditorEmpty(content) {
  if (!content || content === "<p><br></p>") return true;
  const text = content.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").trim();
  return text === "" && !content.includes("<img");
}

function copyText(value) {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(value);
  }

  const input = document.createElement("textarea");
  input.value = value;
  input.style.position = "fixed";
  input.style.opacity = "0";
  document.body.appendChild(input);
  input.select();
  document.execCommand("copy");
  document.body.removeChild(input);
  return Promise.resolve();
}

function BookmarkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [card, setCard] = useState(null);
  const [buckets, setBuckets] = useState([]);
  const [tagOptions, setTagOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [appTheme, setAppTheme] = useState(resolveTheme);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editedTitle, setEditedTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [isNotesFocused, setIsNotesFocused] = useState(false);
  const [isNotesEmpty, setIsNotesEmpty] = useState(true);
  const [isNoteImageUploading, setIsNoteImageUploading] = useState(false);
  const [showNotesToolbar, setShowNotesToolbar] = useState(false);
  const [toolbarPosition, setToolbarPosition] = useState({ top: 0, left: 0 });
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [showColorInput, setShowColorInput] = useState(false);
  const [colorValue, setColorValue] = useState("");
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const [bucketAction, setBucketAction] = useState(null);
  const [selectedBucketId, setSelectedBucketId] = useState("");
  const [isBucketSaving, setIsBucketSaving] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isShareOpen, setIsShareOpen] = useState(false);

  const quillRef = useRef(null);
  const notesContainerRef = useRef(null);
  const noteImageUploadCountRef = useRef(0);
  const shouldSaveNotesAfterImageUploadRef = useRef(false);

  const isDark = appTheme === "dark";
  const sourceUrl = card?.content?.sourceUrl || "";
  const imageUrl = getPrimaryImage(card);
  const isFileCard = card?.cardType?.toLowerCase() === "file" || card?.contentType?.toLowerCase() === "file";
  const fileName = card?.title || imageUrl?.split("/").pop() || "Untitled file";
  const fileExtension = fileName.includes(".")
    ? fileName.split(".").pop().toUpperCase()
    : "FILE";

  const fetchCard = useCallback(async (showLoader = true) => {
    if (showLoader) setIsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_API}cards/single-card/${id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const cardData = response?.data?.data;
      setCard(cardData);
      setEditedTitle(cardData?.title || cardData?.postDetail?.title || "Untitled bookmark");
      const noteContent = cardData?.note || "";
      setNotes(noteContent);
      setIsNotesEmpty(isEditorEmpty(noteContent));
      setTags((cardData?.tags || []).map((item) => item?.name || item).filter(Boolean));
    } catch (error) {
      if (error?.response?.status === 404 || error?.response?.data?.statusCode === 404) {
        navigate("/notFound", { replace: true });
        return;
      }
      toastify("error", getErrorMessage(error, "Unable to load this bookmark."));
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchCard();
  }, [fetchCard]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    Promise.all([
      axios.get(`${process.env.REACT_APP_API}buckets/user-buckets`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${process.env.REACT_APP_API}tags/list`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ])
      .then(([bucketResponse, tagResponse]) => {
        setBuckets(bucketResponse?.data?.data?.bucketList || []);
        setTagOptions(tagResponse?.data?.data || []);
      })
      .catch((error) => console.error("Unable to load bookmark metadata options", error));
  }, []);

  useEffect(() => {
    const syncTheme = () => setAppTheme(resolveTheme());
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const observer = new MutationObserver(syncTheme);

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    window.addEventListener("storage", syncTheme);
    mediaQuery.addEventListener("change", syncTheme);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", syncTheme);
      mediaQuery.removeEventListener("change", syncTheme);
    };
  }, []);

  const bucketChoices = useMemo(
    () =>
      buckets.flatMap((bucket) => [
        { id: bucket._id, name: bucket.name },
        ...(bucket.subBuckets || []).map((subBucket) => ({
          id: subBucket._id,
          name: `${bucket.name} / ${subBucket.name}`,
        })),
      ]),
    [buckets]
  );

  const saveCardFields = async (fields, successMessage) => {
    setIsSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { cardId: id, ...fields },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCard((current) => ({ ...current, ...fields }));
      if (successMessage) toastify("success", response?.data?.message || successMessage);
      return true;
    } catch (error) {
      toastify("error", getErrorMessage(error));
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  const saveTitle = async () => {
    const nextTitle = editedTitle.trim();
    setIsEditingTitle(false);
    if (!nextTitle || nextTitle === card?.title) {
      setEditedTitle(card?.title || "Untitled bookmark");
      return;
    }
    await saveCardFields({ title: nextTitle }, "Title updated.");
  };

  const toggleStar = async () => {
    const nextValue = !card?.starred;
    setCard((current) => ({ ...current, starred: nextValue }));
    const saved = await saveCardFields({ starred: nextValue });
    if (!saved) setCard((current) => ({ ...current, starred: !nextValue }));
  };

  const saveNotes = useCallback(async () => {
    if (noteImageUploadCountRef.current > 0) {
      shouldSaveNotesAfterImageUploadRef.current = true;
      return;
    }

    const content = quillRef.current
      ? quillRef.current.getEditor().root.innerHTML
      : notes;

    try {
      const token = localStorage.getItem("token");
      await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        { cardId: id, note: content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotes(content);
      setCard((current) => ({ ...current, note: content }));
      toastify("success", "Notes saved successfully");
    } catch (error) {
      console.error("Failed to save notes:", error);
      toastify("error", "Failed to save notes");
    }
  }, [id, notes]);

  const uploadAndInsertNoteImage = async (file) => {
    if (!file?.type?.startsWith("image/")) return;

    noteImageUploadCountRef.current += 1;
    setIsNoteImageUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("type", "other");

      const response = await services.uploadImage(formData, false);
      const uploadedAsset = response?.data?.asset || response?.asset || response?.data || response;
      const uploadedImageUrl = uploadedAsset?.url || response?.data?.url || response?.url;

      if (!uploadedImageUrl) {
        throw new Error("The image upload response did not include a URL.");
      }

      if (quillRef.current) {
        const quill = quillRef.current.getEditor();
        const range = quill.getSelection() || { index: quill.getLength() };
        quill.insertEmbed(range.index, "image", uploadedImageUrl);
        if (quill.hasFocus()) quill.setSelection(range.index + 1, 0, "silent");
        setIsNotesEmpty(false);
      }
    } catch (error) {
      console.error("Failed to upload note image:", error);
      toastify("error", "Failed to upload image. Please try again.");
    } finally {
      noteImageUploadCountRef.current -= 1;
      if (noteImageUploadCountRef.current === 0) {
        setIsNoteImageUploading(false);
        if (shouldSaveNotesAfterImageUploadRef.current) {
          shouldSaveNotesAfterImageUploadRef.current = false;
          saveNotes();
        }
      }
    }
  };

  const handleNotesPaste = (event) => {
    const items = (event.clipboardData || window.clipboardData)?.items || [];
    for (let index = 0; index < items.length; index += 1) {
      if (items[index].type.includes("image")) {
        event.preventDefault();
        uploadAndInsertNoteImage(items[index].getAsFile());
        return;
      }
    }
  };

  const handleNotesDrop = (event) => {
    const file = event.dataTransfer.files?.[0];
    if (file?.type?.startsWith("image/")) {
      event.preventDefault();
      uploadAndInsertNoteImage(file);
    }
  };

  const handleTextSelection = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0 && selection.toString().trim()) {
      const rect = selection.getRangeAt(0).getBoundingClientRect();
      setToolbarPosition({ top: rect.top - 72, left: rect.left + rect.width / 2 - 150 });
      setShowNotesToolbar(true);
    } else {
      setShowNotesToolbar(false);
      setShowLinkInput(false);
      setShowColorInput(false);
    }
  };

  const formatText = (command, value = null) => {
    if (!quillRef.current) return;
    const editor = quillRef.current.getEditor();
    editor.focus();
    const mapping = {
      bold: "bold",
      italic: "italic",
      underline: "underline",
      strikeThrough: "strike",
      insertUnorderedList: "list",
      createLink: "link",
      foreColor: "color",
    };
    const format = mapping[command] || command;

    if (command === "insertUnorderedList") {
      const currentFormat = editor.getFormat();
      editor.format("list", currentFormat.list === "bullet" ? false : "bullet");
    } else if (command === "createLink") {
      if (value) editor.format("link", value);
    } else {
      const currentFormat = editor.getFormat();
      editor.format(format, currentFormat[format] ? false : value || true);
    }
    handleTextSelection();
  };

  const applyLink = () => {
    if (!linkUrl) return;
    formatText("createLink", linkUrl);
    setLinkUrl("");
    setShowLinkInput(false);
    setShowNotesToolbar(false);
  };

  const applyColor = () => {
    if (!colorValue) return;
    formatText("foreColor", colorValue);
    setColorValue("");
    setShowColorInput(false);
    setShowNotesToolbar(false);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notesContainerRef.current && !notesContainerRef.current.contains(event.target) && isNotesFocused) {
        setIsNotesFocused(false);
        setShowNotesToolbar(false);
        if (noteImageUploadCountRef.current > 0) {
          shouldSaveNotesAfterImageUploadRef.current = true;
        } else {
          saveNotes();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isNotesFocused, saveNotes]);

  const updateTags = async (newTags) => {
    const normalizedTags = [...new Set(newTags.map((item) => item.trim()).filter(Boolean))];
    setTags(normalizedTags);
    await saveCardFields({ tags: normalizedTags.map((name) => ({ name })) });
  };

  const handleTagKeyDown = (event) => {
    if ((event.key === "Enter" || event.key === ",") && tagInput.trim()) {
      event.preventDefault();
      if (!tags.includes(tagInput.trim())) updateTags([...tags, tagInput.trim()]);
      setTagInput("");
    }
  };

  const openBucketModal = (action) => {
    setSelectedBucketId("");
    setBucketAction(action);
  };

  const applyBucketAction = async () => {
    if (!selectedBucketId) {
      toastify("error", "Please select a destination bucket.");
      return;
    }

    setIsBucketSaving(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.patch(
        `${process.env.REACT_APP_API}cards/update`,
        {
          optionType: bucketAction,
          destinationBucketID: selectedBucketId,
          cardId: id,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toastify("success", response?.data?.message);
      setBucketAction(null);
      setSelectedBucketId("");
      if (bucketAction === "MoveTo") await fetchCard(false);
    } catch (error) {
      toastify("error", getErrorMessage(error));
    } finally {
      setIsBucketSaving(false);
    }
  };

  const deleteBookmark = async () => {
    setIsDeleting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${process.env.REACT_APP_API}cards/delete`, {
        data: { deletedIds: id },
        headers: { Authorization: `Bearer ${token}` },
      });
      toastify("success", response?.data?.message);
      navigate("/app/bookmarks", { replace: true });
    } catch (error) {
      toastify("error", getErrorMessage(error));
      setIsDeleting(false);
    }
  };

  const copySourceLink = async () => {
    if (!sourceUrl) return;
    try {
      await copyText(sourceUrl);
      toastify("success", "Bookmark link copied.");
    } catch {
      toastify("error", "Unable to copy the bookmark link.");
    }
  };

  const copyPublicLink = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API}cards/public-url`,
        { itemId: id, isPublic: true, itemType: "Card" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response?.data?.data) {
        await copyText(response.data.data);
        toastify("success", response?.data?.message || "Public link copied.");
      }
    } catch (error) {
      toastify("error", getErrorMessage(error));
    }
  };

  const downloadPreview = async () => {
    if (!imageUrl || imageUrl === BookmarkPreview) {
      toastify("error", "No preview image is available to download.");
      return;
    }

    setIsDownloading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${process.env.REACT_APP_API}cards/download-file`,
        { fileUrl: imageUrl },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const base64Data = response?.data?.data?.fileBase64;
      if (!base64Data) throw new Error("No file data returned");

      const bytes = Uint8Array.from(atob(base64Data), (character) => character.charCodeAt(0));
      let extension = isFileCard ? fileExtension.toLowerCase() : "png";
      try {
        const candidate = new URL(imageUrl).pathname.split(".").pop()?.toLowerCase();
        if (isFileCard && candidate) {
          extension = candidate;
        } else if (["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(candidate)) {
          extension = candidate;
        }
      } catch {
        // Keep the file extension or png fallback when the URL cannot be parsed.
      }
      const mimeType = isFileCard
        ? "application/octet-stream"
        : extension === "svg"
          ? "image/svg+xml"
          : `image/${extension === "jpg" ? "jpeg" : extension}`;
      const objectUrl = URL.createObjectURL(new Blob([bytes], { type: mimeType }));
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = isFileCard
        ? fileName
        : `${(card?.title || "bookmark").replace(/[^a-z0-9]/gi, "_").toLowerCase()}.${extension}`;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(objectUrl);
      toastify("success", isFileCard ? "File downloaded." : "Preview downloaded.");
    } catch (error) {
      toastify("error", getErrorMessage(error, isFileCard ? "Unable to download the file." : "Unable to download the preview."));
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`bookmark-detail-page ${isDark ? "is-dark" : "is-light"}`}>
        <div className="App-loading bookmark-detail-logo-loading">
          <div className="App-logo" title="Zimzee" />
        </div>
      </div>
    );
  }

  if (!card) return null;

  return (
    <div className={`bookmark-detail-page ${isDark ? "is-dark" : "is-light"}`}>
      <header className="bookmark-detail-breadcrumb">
        <button type="button" onClick={() => navigate("/app/bookmarks")}>
          <ChevronLeft />
          Bookmarks
        </button>
        <span>/</span>
        <strong>{card.title || "Untitled bookmark"}</strong>
      </header>

      <main className="bookmark-detail-main">
        <section className={`bookmark-detail-preview-panel ${isFullscreen ? "fullscreen" : ""}`}>
          <div className="bookmark-detail-actionbar">
            <div>
              <button type="button" onClick={() => openBucketModal("MoveTo")}>
                <FolderInput /> Move to
              </button>
              <button type="button" onClick={() => openBucketModal("CopyTo")}>
                <Copy /> Copy to
              </button>
              <button type="button" className="danger" onClick={() => setIsDeleteOpen(true)}>
                <Trash2 /> Delete
              </button>
            </div>
            <div>
              <button
                type="button"
                onClick={() => window.open(sourceUrl, "_blank", "noopener,noreferrer")}
                disabled={!sourceUrl}
              >
                <ExternalLink /> Original
              </button>
              <button type="button" className="invite" onClick={() => setIsShareOpen(true)}>
                <Users /> Invite
              </button>
            </div>
          </div>

          <div className="bookmark-detail-preview-scroll">
            <div className="bookmark-detail-preview-content">
              <div className="bookmark-detail-title-row">
                <div className="bookmark-detail-site-icon"><Globe /></div>
                <div className="bookmark-detail-title-copy">
                  {isEditingTitle ? (
                    <input
                      value={editedTitle}
                      onChange={(event) => setEditedTitle(event.target.value)}
                      onBlur={saveTitle}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") event.currentTarget.blur();
                        if (event.key === "Escape") {
                          setEditedTitle(card.title || "Untitled bookmark");
                          setIsEditingTitle(false);
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    <h1 onClick={() => setIsEditingTitle(true)}>{card.title || "Untitled bookmark"}</h1>
                  )}
                  <p>{getDomain(sourceUrl) || "Saved bookmark"}</p>
                </div>
                <div className="bookmark-detail-title-actions">
                  <button type="button" onClick={() => setIsEditingTitle(true)} aria-label="Edit title">
                    <Pencil />
                  </button>
                  <button
                    type="button"
                    className={card.starred ? "starred" : ""}
                    onClick={toggleStar}
                    aria-label={card.starred ? "Remove from starred" : "Add to starred"}
                  >
                    <Star className={card.starred ? "fill-current" : ""} />
                  </button>
                  <button type="button" onClick={() => setIsFullscreen((value) => !value)} aria-label="Toggle fullscreen">
                    {isFullscreen ? <Minimize2 /> : <Maximize2 />}
                  </button>
                </div>
              </div>

              <div className="bookmark-detail-image-card">
                {isFileCard ? (
                  <div className="bookmark-detail-file-preview">
                    <div className="bookmark-detail-file-icon"><FileText /></div>
                    <strong title={fileName}>{fileName}</strong>
                    <span>{fileExtension} FILE</span>
                  </div>
                ) : (
                  <img src={imageUrl} alt={card.title || "Bookmark preview"} />
                )}
              </div>

              <button
                type="button"
                className="bookmark-detail-download"
                onClick={downloadPreview}
                disabled={isDownloading || imageUrl === BookmarkPreview}
              >
                {isDownloading ? <Loader2 className="spin" /> : <Download />}
                {isDownloading ? "Downloading..." : isFileCard ? "Download file" : "Download"}
              </button>

              <div className="bookmark-detail-meta">
                <div>
                  <Calendar />
                  <span><small>Created</small><strong>{formatDate(card.createdAt)}</strong></span>
                </div>
                <div>
                  <Clock />
                  <span><small>Updated</small><strong>{formatDate(card.updatedAt)}</strong></span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {!isFullscreen && (
          <aside className="bookmark-detail-sidebar">
            <div className="bookmark-detail-sidebar-scroll">
              <section className="bookmark-detail-section">
                <div className="bookmark-detail-section-heading">
                  <h2>Notes</h2>
                  <Pencil />
                </div>
                <div className="bookmark-detail-notes" ref={notesContainerRef}>
                  {isNotesEmpty && !isNotesFocused && (
                    <div className="bookmark-detail-notes-placeholder">
                      <div>Click here to start typing…</div>
                      <div>Highlight text to format — click outside to save.</div>
                      <div>You can also drag or paste images right into this space.</div>
                    </div>
                  )}

                  {isNoteImageUploading && (
                    <div className="bookmark-detail-notes-uploading">
                      <Loader2 className="spin" /> Uploading image…
                    </div>
                  )}

                  <div
                    onMouseUp={handleTextSelection}
                    onClick={(event) => {
                      if (event.target.closest("a")) return;
                      if (!isNotesFocused) {
                        setIsNotesFocused(true);
                        quillRef.current?.getEditor().focus();
                      }
                    }}
                    onPaste={handleNotesPaste}
                    onDrop={handleNotesDrop}
                    onDragOver={(event) => event.preventDefault()}
                  >
                    <ReactQuill
                      ref={quillRef}
                      theme="snow"
                      value={notes}
                      onChange={(content, delta, source, editor) => {
                        setNotes(content);
                        setIsNotesEmpty(
                          !content ||
                            content === "<p><br></p>" ||
                            (editor.getText().trim() === "" && !content.includes("<img"))
                        );
                      }}
                      modules={{
                        toolbar: false,
                        clipboard: { matchVisual: false },
                      }}
                      onFocus={() => setIsNotesFocused(true)}
                      className="bookmark-detail-notes-editor"
                    />
                  </div>

                  {showNotesToolbar && (
                    <div
                      className="bookmark-detail-notes-toolbar"
                      style={{ top: `${toolbarPosition.top}px`, left: `${toolbarPosition.left}px` }}
                    >
                      {!showLinkInput && !showColorInput && (
                        <div className="bookmark-detail-notes-toolbar-actions">
                          <button type="button" onClick={() => formatText("bold")} title="Bold"><strong>B</strong></button>
                          <button type="button" onClick={() => formatText("italic")} title="Italic"><em>I</em></button>
                          <button type="button" onClick={() => formatText("underline")} title="Underline"><span className="bookmark-detail-notes-underlined">U</span></button>
                          <button type="button" onClick={() => formatText("strikeThrough")} title="Strikethrough"><span className="bookmark-detail-notes-strikethrough">S</span></button>
                          <span className="bookmark-detail-notes-toolbar-divider" />
                          <button type="button" onClick={() => formatText("insertUnorderedList")} title="Bullet list"><List /></button>
                          <span className="bookmark-detail-notes-toolbar-divider" />
                          <button type="button" onClick={() => setShowLinkInput(true)} title="Add link"><LinkIcon /></button>
                          <button type="button" onClick={() => setShowColorInput(true)} title="Text color"><span className="bookmark-detail-notes-color-icon" /></button>
                        </div>
                      )}

                      {showLinkInput && (
                        <div className="bookmark-detail-notes-toolbar-input">
                          <input
                            type="url"
                            placeholder="Enter URL..."
                            value={linkUrl}
                            onChange={(event) => setLinkUrl(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                applyLink();
                              } else if (event.key === "Escape") {
                                setShowLinkInput(false);
                                setLinkUrl("");
                              }
                            }}
                            autoFocus
                          />
                          <button type="button" className="apply" onClick={applyLink}>Apply</button>
                          <button type="button" onClick={() => { setShowLinkInput(false); setLinkUrl(""); }} aria-label="Cancel"><X /></button>
                        </div>
                      )}

                      {showColorInput && (
                        <div className="bookmark-detail-notes-toolbar-input">
                          <input
                            type="text"
                            placeholder="e.g. #ff0000, red"
                            value={colorValue}
                            onChange={(event) => setColorValue(event.target.value)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter") {
                                event.preventDefault();
                                applyColor();
                              } else if (event.key === "Escape") {
                                setShowColorInput(false);
                                setColorValue("");
                              }
                            }}
                            autoFocus
                          />
                          <button type="button" className="apply" onClick={applyColor}>Apply</button>
                          <button type="button" onClick={() => { setShowColorInput(false); setColorValue(""); }} aria-label="Cancel"><X /></button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </section>

              <section className="bookmark-detail-section">
                <h2>Tags</h2>
                {tags.length > 0 && (
                  <div className="bookmark-detail-tags">
                    {tags.map((tagName) => (
                      <span key={tagName}>
                        {tagName}
                        <button type="button" onClick={() => updateTags(tags.filter((item) => item !== tagName))} aria-label={`Remove ${tagName}`}>
                          <X />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <label className="bookmark-detail-tag-input">
                  <Tag />
                  <input
                    value={tagInput}
                    onChange={(event) => setTagInput(event.target.value)}
                    onKeyDown={handleTagKeyDown}
                    placeholder="Add a tag..."
                    list="bookmark-detail-tag-options"
                  />
                </label>
                <datalist id="bookmark-detail-tag-options">
                  {tagOptions
                    .filter((option) => !tags.includes(option.name))
                    .map((option) => <option key={option._id} value={option.name} />)}
                </datalist>
                <p className="bookmark-detail-help">Separate tags with comma or press Enter</p>
              </section>

              <section className="bookmark-detail-section">
                <h2>Bucket</h2>
                {card?.bucketId?.name ? (
                  <button type="button" className="bookmark-detail-bucket" onClick={() => openBucketModal("MoveTo")}>
                    <span><FolderInput /></span>
                    <strong>{card.bucketId.name}</strong>
                  </button>
                ) : (
                  <button type="button" className="bookmark-detail-empty-action" onClick={() => openBucketModal("MoveTo")}>
                    <Plus /> Add to bucket
                  </button>
                )}
              </section>

              <section className="bookmark-detail-section">
                <h2>Link</h2>
                <div className="bookmark-detail-link">
                  <LinkIcon />
                  <span title={sourceUrl}>{sourceUrl || "No source link"}</span>
                  <button type="button" onClick={copySourceLink} disabled={!sourceUrl} aria-label="Copy source link"><Copy /></button>
                  <button
                    type="button"
                    onClick={() => window.open(sourceUrl, "_blank", "noopener,noreferrer")}
                    disabled={!sourceUrl}
                    aria-label="Open source link"
                  >
                    <ExternalLink />
                  </button>
                </div>
                <button type="button" className="bookmark-detail-public-link" onClick={copyPublicLink}>
                  <LinkIcon /> Copy public link
                </button>
              </section>

              <section className="bookmark-detail-section">
                <h2>Sharing</h2>
                <button type="button" className="bookmark-detail-empty-action" onClick={() => setIsShareOpen(true)}>
                  <Share2 /> Share this bookmark
                </button>
              </section>
            </div>
          </aside>
        )}
      </main>

      <Transition appear show={Boolean(bucketAction)} as={Fragment}>
        <Dialog
          as="div"
          className={`bookmark-detail-dialog-root ${isDark ? "is-dark" : "is-light"}`}
          onClose={() => !isBucketSaving && setBucketAction(null)}
        >
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="bookmark-detail-dialog-backdrop" />
          </Transition.Child>
          <div className="bookmark-detail-dialog-scroll">
            <div className="bookmark-detail-dialog-center">
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="bookmark-detail-dialog">
                  <div className="bookmark-detail-dialog-header">
                    <div>
                      <Dialog.Title>{bucketAction === "MoveTo" ? "Move to bucket" : "Copy to bucket"}</Dialog.Title>
                      <p>Choose a destination for this bookmark.</p>
                    </div>
                    <button type="button" onClick={() => setBucketAction(null)}><X /></button>
                  </div>
                  <label className="bookmark-detail-dialog-field">
                    <span>Destination bucket</span>
                    <select value={selectedBucketId} onChange={(event) => setSelectedBucketId(event.target.value)} autoFocus>
                      <option value="">Select a bucket</option>
                      {bucketChoices.map((bucket) => <option key={bucket.id} value={bucket.id}>{bucket.name}</option>)}
                    </select>
                  </label>
                  <div className="bookmark-detail-dialog-actions">
                    <button type="button" className="secondary" onClick={() => setBucketAction(null)} disabled={isBucketSaving}>Cancel</button>
                    <button type="button" className="primary" onClick={applyBucketAction} disabled={isBucketSaving}>
                      {isBucketSaving ? <Loader2 className="spin" /> : <Check />}
                      {isBucketSaving ? "Saving..." : bucketAction === "MoveTo" ? "Move" : "Copy"}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <Transition appear show={isDeleteOpen} as={Fragment}>
        <Dialog
          as="div"
          className={`bookmark-detail-dialog-root ${isDark ? "is-dark" : "is-light"}`}
          onClose={() => !isDeleting && setIsDeleteOpen(false)}
        >
          <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="bookmark-detail-dialog-backdrop" />
          </Transition.Child>
          <div className="bookmark-detail-dialog-scroll">
            <div className="bookmark-detail-dialog-center">
              <Transition.Child as={Fragment} enter="ease-out duration-200" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-in duration-150" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
                <Dialog.Panel className="bookmark-detail-dialog bookmark-detail-delete-dialog">
                  <div className="bookmark-detail-delete-icon"><Trash2 /></div>
                  <Dialog.Title>Delete bookmark?</Dialog.Title>
                  <p>This action cannot be undone.</p>
                  <div className="bookmark-detail-dialog-actions">
                    <button type="button" className="secondary" onClick={() => setIsDeleteOpen(false)} disabled={isDeleting}>Cancel</button>
                    <button type="button" className="delete" onClick={deleteBookmark} disabled={isDeleting}>
                      {isDeleting ? "Deleting..." : "Delete"}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {isShareOpen && (
        <ShareCard
          shareBucketId={id}
          closeSharePop={() => setIsShareOpen(false)}
          cardsRefresh={() => fetchCard(false)}
        />
      )}

      {isSaving && <div className="bookmark-detail-saving"><Loader2 className="spin" /> Saving</div>}
      <ToastContainer />
    </div>
  );
}

export default BookmarkDetail;
